import React, {
  PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/**
 * Provides an easy way to animate collapsing elements that don't have explicit heights
 *
 * Note: if you know up ahead the height of your element, just using css transition
 * is enough for you
 */
export const AutoAnimatedHeight = ({
  children,
  visible,
  alwaysRendered = true,
}: PropsWithChildren<{
  /**
   * Whether to always render the given children, setting this to false will cause
   * the children to disappear before the transition is finished
   *
   * @default true
   */
  alwaysRendered?: boolean;

  /**
   * If the children is visible or not
   *
   * note: this does not control if the children is rendered
   */
  visible: boolean;
}>) => {
  const animContainer = useRef<HTMLDivElement>(null);
  const heightContainer = useRef<HTMLDivElement | null>(null);
  const [initialized, setInitialized] = useState(false);
  const render = alwaysRendered || visible;

  useLayoutEffect(() => {
    if (initialized && heightContainer.current && animContainer.current) {
      const rect = heightContainer.current.getBoundingClientRect();
      const effectiveH = alwaysRendered && !visible ? 0 : rect.height;

      animContainer.current.style.height = `${effectiveH}px`;
    }
  }, [alwaysRendered, initialized, visible]);

  return (
    <div ref={animContainer} className="anim-container">
      <div
        className="abs-container"
        ref={(ref) => {
          heightContainer.current = ref;
          setInitialized(true);
        }}
      >
        {render && children}
      </div>
      <style jsx>
        {`
          .abs-container {
            position: absolute;
            width: 100%;
            top: 0;
          }
          .anim-container {
            overflow-y: hidden;
            position: relative;
            height: 0;
            transition: height 0.3s;
          }
        `}
      </style>
    </div>
  );
};
